import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { getAvailableProducts, getProductCategories, type Product, type Category as ProductCategory } from '../../services/shopService';
import ProductCard from '../../components/shop/ProductCard';
import { SEOMetaTags } from '../../components/shared/SEOMetaTags';
import { Filter, Search, X, ChevronDown, Loader2, ArrowRight, ShoppingBag, Sparkles } from 'lucide-react';
import * as LucideIcons from 'lucide-react';
import { PublicLayout } from '../../components/layout/PublicLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { useTenantContext } from '../../context/TenantContext';

// Mapping d'icônes pour personnalisation - Utilise toutes les icônes Lucide
const iconMap = LucideIcons as Record<string, React.ElementType>;

function hexToRgba(hex: string, alpha = 1) {
  const cleaned = hex.replace('#', '');
  const bigint = parseInt(cleaned.length === 3
    ? cleaned.split('').map((c) => c + c).join('')
    : cleaned, 16);
  const r = (bigint >> 16) & 255;
  const g = (bigint >> 8) & 255;
  const b = bigint & 255;
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

const containerVariants = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.08 } }
};

const cardVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5, ease: "easeOut" } }
};

const ProductListPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [products, setProducts] = useState<Product[]>([]);
  const [categories, setCategories] = useState<ProductCategory[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  
  // Filtres avancés
  const [searchInput, setSearchInput] = useState('');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(window.innerWidth >= 768);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [availableMaterials, setAvailableMaterials] = useState<string[]>([]);
  const [selectedMaterials, setSelectedMaterials] = useState<string[]>([]);
  const [availableYears, setAvailableYears] = useState<number[]>([]);
  const [selectedYears, setSelectedYears] = useState<number[]>([]);
  // Contrôle de l'affichage "voir plus" pour chaque filtre
  const [showAllCategories, setShowAllCategories] = useState(false);
  const [showAllMaterials, setShowAllMaterials] = useState(false);
  const [showAllYears, setShowAllYears] = useState(false);
  
  // Filtre par poids et quantité
  const [weightRange, setWeightRange] = useState<{min: number | null, max: number | null}>({min: null, max: null});
  const [stockRange, setStockRange] = useState<{min: number | null, max: number | null}>({min: null, max: null});
  const [priceRange, setPriceRange] = useState<{min: number | null, max: number | null}>({min: null, max: null});
  const [isUniqueOnly, setIsUniqueOnly] = useState<boolean | null>(null);

  const categorySlug = searchParams.get('categorie') || 'all';
  const sortBy = (searchParams.get('tri') as 'created_at' | 'price' | 'title') || 'created_at';
  const sortOrder = searchParams.get('ordre') as 'asc' | 'desc' || 'desc';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = 12;

  const { tenant, previewConfig } = useTenantContext();

  // Configuration de la grille de produits
  const gridConfig = useMemo(() => {
    const config = previewConfig?.pages?.shop?.productCard || tenant?.config?.pages?.shop?.productCard || {};
    return {
      colsMobile: config.gridColsMobile || '1',
      colsTablet: config.gridColsTablet || '2',
      colsDesktop: config.gridColsDesktop || '3',
      gap: config.gridGap || '16',
    };
  }, [tenant, previewConfig]);

  // Générer la className avec toutes les classes possibles (pour que Tailwind les détecte)
  const gridClassName = useMemo(() => {
    // Map de toutes les combinaisons possibles
    const gridMap: Record<string, string> = {
      '1-1-1': 'grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-1',
      '1-1-2': 'grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-2',
      '1-1-3': 'grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-3',
      '1-1-4': 'grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-4',
      '1-1-5': 'grid grid-cols-1 sm:grid-cols-1 lg:grid-cols-5',
      '1-2-1': 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1',
      '1-2-2': 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2',
      '1-2-3': 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3',
      '1-2-4': 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4',
      '1-2-5': 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5',
      '1-3-1': 'grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-1',
      '1-3-2': 'grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-2',
      '1-3-3': 'grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-3',
      '1-3-4': 'grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-4',
      '1-3-5': 'grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5',
      '1-4-1': 'grid grid-cols-1 sm:grid-cols-4 lg:grid-cols-1',
      '1-4-2': 'grid grid-cols-1 sm:grid-cols-4 lg:grid-cols-2',
      '1-4-3': 'grid grid-cols-1 sm:grid-cols-4 lg:grid-cols-3',
      '1-4-4': 'grid grid-cols-1 sm:grid-cols-4 lg:grid-cols-4',
      '1-4-5': 'grid grid-cols-1 sm:grid-cols-4 lg:grid-cols-5',
      '2-1-1': 'grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-1',
      '2-1-2': 'grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-2',
      '2-1-3': 'grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-3',
      '2-1-4': 'grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-4',
      '2-1-5': 'grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-5',
      '2-2-1': 'grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-1',
      '2-2-2': 'grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-2',
      '2-2-3': 'grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3',
      '2-2-4': 'grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4',
      '2-2-5': 'grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-5',
      '2-3-1': 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-1',
      '2-3-2': 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-2',
      '2-3-3': 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-3',
      '2-3-4': 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4',
      '2-3-5': 'grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5',
      '2-4-1': 'grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-1',
      '2-4-2': 'grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-2',
      '2-4-3': 'grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-3',
      '2-4-4': 'grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-4',
      '2-4-5': 'grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-5',
    };
    
    const key = `${gridConfig.colsMobile}-${gridConfig.colsTablet}-${gridConfig.colsDesktop}`;
    return gridMap[key] || 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3';
  }, [gridConfig]);

  // Init recherche depuis l'URL (support Bottom Header: /boutique?q=...)
  useEffect(() => {
    try {
      const q = (new URLSearchParams(window.location.search)).get('q') || '';
      if (q) setSearchInput(q);
    } catch {}
    // Une seule fois au montage suffit
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Thème Boutique (preview > tenant)
  const shopTheme = React.useMemo(() => {
    const base = (previewConfig?.theme?.notFound) ?? (tenant?.config?.theme?.notFound) ?? ({} as any);
    const themeColors = {
      c1: (tenant?.config?.color1 as string) || '#fe8d17', // primaire (bouton)
      c2: (tenant?.config?.color2 as string) || '#008aef', // secondaire (bouton)
      c3: (tenant?.config?.color3 as string) || '#ffffff',
      c4: (tenant?.config?.color4 as string) || '#9ca3af',
    };
    const defaults = {
      overlay: {
        enabled: true,
        useGradient: true,
        orientation: 'to-b', // to-b, to-t, to-r, to-l, to-tr, to-br, to-tl, to-bl
        color1: '#08062b',
        color2: '#08062b',
        color3: '#08062b',
        opacity1: 0.95,
        opacity2: 0.80,
        opacity3: 0.95,
      },
      icons: [
        { enabled: true, icon: 'ShoppingBag', color: themeColors.c1, size: 32 }, // top-left
        { enabled: true, icon: 'Sparkles',   color: themeColors.c2, size: 28 }, // top-right
        { enabled: true, icon: 'Diamond',    color: themeColors.c2, size: 24 }, // bottom-left
        { enabled: true, icon: 'Star',       color: themeColors.c1, size: 30 }, // bottom-right
      ]
    } as any;
    return {
      overlay: {
        enabled: base.overlay?.enabled ?? defaults.overlay.enabled,
        useGradient: base.overlay?.useGradient ?? defaults.overlay.useGradient,
        orientation: base.overlay?.orientation ?? defaults.overlay.orientation,
        color1: base.overlay?.color1 ?? defaults.overlay.color1,
        color2: base.overlay?.color2 ?? defaults.overlay.color2,
        color3: base.overlay?.color3 ?? defaults.overlay.color3,
        opacity1: base.overlay?.opacity1 ?? defaults.overlay.opacity1,
        opacity2: base.overlay?.opacity2 ?? defaults.overlay.opacity2,
        opacity3: base.overlay?.opacity3 ?? defaults.overlay.opacity3,
      },
      icons: (base.icons && Array.isArray(base.icons) && base.icons.length === 4)
        ? base.icons.map((i: any, idx: number) => ({
            enabled: i?.enabled ?? defaults.icons[idx].enabled,
            icon: i?.icon ?? defaults.icons[idx].icon,
            color: i?.color ?? defaults.icons[idx].color,
            size: Number(i?.size ?? defaults.icons[idx].size),
          }))
        : defaults.icons,
    };
  }, [tenant, previewConfig]);

  const shopHeader = React.useMemo(() => {
    const source = (previewConfig as any)?.pages?.shop?.header
      ?? (tenant?.config as any)?.pages?.shop?.header
      ?? {};
    const defaults = {
      badgeLabel: 'Boutique SIS',
      title: `Produits ${tenant?.name || 'SIS'}`,
      line1: 'Découvrez notre sélection de produits et services informatiques.',
      line2: 'Trouvez la solution adaptée à vos besoins.',
    };
    return {
      badgeLabel: source?.badgeLabel ?? defaults.badgeLabel,
      title: source?.title ?? defaults.title,
      line1: source?.line1 ?? defaults.line1,
      line2: source?.line2 ?? defaults.line2,
    };
  }, [previewConfig, tenant?.config, tenant?.name]);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const categoriesData = await getProductCategories();
        setCategories(categoriesData);

        const { products: productsData, count } = await getAvailableProducts({
          categorySlug: categorySlug === 'all' ? undefined : categorySlug,
          sortBy,
          sortOrder,
          limit,
          offset: (page - 1) * limit
        });
        setProducts(productsData);
        setTotalCount(count || 0);
        
        // Extraire les matériaux disponibles
        const materials = [...new Set(productsData.flatMap(product => product.materials || []))].filter(Boolean);
        setAvailableMaterials(materials);
        
        // Extraire les années disponibles
        const years = [...new Set(productsData.map(product => product.year).filter(Boolean))];
        setAvailableYears(years.sort((a, b) => b - a));
      } catch (error) {
        console.error('Erreur lors du chargement des produits:', error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [categorySlug, sortBy, sortOrder, page]);
  
  // Filtrer les produits avec la recherche et les filtres
  const filteredProducts = useMemo(() => {
    return products.filter(product => {
      // Filtre de recherche
      const searchMatch = !searchInput || 
        product.title.toLowerCase().includes(searchInput.toLowerCase()) || 
        product.description?.toLowerCase().includes(searchInput.toLowerCase());
      
      // Filtre par catégorie
      const categoryMatch = selectedCategories.length === 0 || 
        selectedCategories.includes(product.category?.id.toString() || '');
      
      // Filtre par matériau
      const materialMatch = selectedMaterials.length === 0 || 
        product.materials?.some(mat => selectedMaterials.includes(mat));
      
      // Filtre par année
      const yearMatch = selectedYears.length === 0 || 
        (product.year && selectedYears.includes(product.year));
      
      // Filtre par prix
      const priceMatch = 
        (!priceRange.min || (product.price && product.price >= priceRange.min)) && 
        (!priceRange.max || (product.price && product.price <= priceRange.max));
        
      // Filtre par poids
      const weightMatch = 
        (!weightRange.min || (product.weight_kg && product.weight_kg >= weightRange.min)) && 
        (!weightRange.max || (product.weight_kg && product.weight_kg <= weightRange.max));
      
      // Filtre par quantité en stock
      const stockMatch = 
        (!stockRange.min || (product.stock_quantity && product.stock_quantity >= stockRange.min)) && 
        (!stockRange.max || (product.stock_quantity && product.stock_quantity <= stockRange.max));
      
      // Filtre par type (unique ou non)
      const uniqueMatch = isUniqueOnly === null || product.is_unique === isUniqueOnly;
      
      return searchMatch && categoryMatch && materialMatch && yearMatch && priceMatch && weightMatch && stockMatch && uniqueMatch;
    });
  }, [products, searchInput, selectedCategories, selectedMaterials, selectedYears, priceRange, weightRange, stockRange, isUniqueOnly]);

  const handleFilterChange = (param: string, value: string) => {
    const newParams = new URLSearchParams(searchParams.toString());
    newParams.set(param, value);
    if (param !== 'page') newParams.set('page', '1');
    setSearchParams(newParams);
  };
  
  const handleCategoryToggle = (categoryId: string) => {
    setSelectedCategories(prev => 
      prev.includes(categoryId) 
        ? prev.filter(id => id !== categoryId) 
        : [...prev, categoryId]
    );
  };
  
  const handleMaterialToggle = (material: string) => {
    setSelectedMaterials(prev => 
      prev.includes(material) 
        ? prev.filter(mat => mat !== material) 
        : [...prev, material]
    );
  };
  
  const handleYearToggle = (year: number) => {
    setSelectedYears(prev => 
      prev.includes(year) 
        ? prev.filter(y => y !== year) 
        : [...prev, year]
    );
  };
  
  const handlePriceRangeChange = (type: 'min' | 'max', value: string) => {
    const numValue = value === '' ? null : Number(value);
    setPriceRange(prev => ({ ...prev, [type]: numValue }));
  };
  
  const handleWeightRangeChange = (type: 'min' | 'max', value: string) => {
    const numValue = value === '' ? null : Number(value);
    setWeightRange(prev => ({ ...prev, [type]: numValue }));
  };
  
  const handleStockRangeChange = (type: 'min' | 'max', value: string) => {
    const numValue = value === '' ? null : Number(value);
    setStockRange(prev => ({ ...prev, [type]: numValue }));
  };
  
  const handleUniqueToggle = (value: boolean | null) => {
    setIsUniqueOnly(prev => prev === value ? null : value);
  };
  
  const handleSortChange = (value: string) => {
    const [newSortBy, newSortOrder] = value.split('-');
    handleFilterChange('tri', newSortBy);
    handleFilterChange('ordre', newSortOrder);
  };

  const resetFilters = () => {
    setSearchInput('');
    setSelectedCategories([]);
    setSelectedMaterials([]);
    setSelectedYears([]);
    setPriceRange({min: null, max: null});
    setWeightRange({min: null, max: null});
    setStockRange({min: null, max: null});
    setIsUniqueOnly(null);
    setSearchParams({});
  };
  
  const hasActiveFilters = searchInput || 
    selectedCategories.length > 0 || 
    selectedMaterials.length > 0 || 
    selectedYears.length > 0 || 
    priceRange.min !== null || 
    priceRange.max !== null || 
    weightRange.min !== null || 
    weightRange.max !== null || 
    stockRange.min !== null || 
    stockRange.max !== null || 
    isUniqueOnly !== null;

  const totalPages = Math.ceil(totalCount / limit);

  // Configuration SEO personnalisée par tenant
  const seoConfig = React.useMemo(() => {
    const seo = tenant?.config?.seo || {};
    const shopHeader = tenant?.config?.pages?.shop?.header || {};
    const currentDomain = window.location.hostname;
    
    return {
      title: `${shopHeader.title || 'Boutique'} - ${tenant?.name || 'Boutique'}`,
      description: seo.description || shopHeader.line1 || `Découvrez notre sélection de produits sur ${tenant?.name || 'notre boutique'}.`,
      image: seo.ogImage || tenant?.logo || tenant?.icon,
      url: `https://${currentDomain}/boutique`
    };
  }, [tenant]);

  return (
    <PublicLayout>
      <SEOMetaTags
        title={seoConfig.title}
        description={seoConfig.description}
        image={seoConfig.image}
        url={seoConfig.url}
      />
      
      <div className="min-h-screen relative overflow-hidden">
      {/* Fond avec la nouvelle palette de couleurs SIS */}
      <div 
        className="fixed inset-0 z-0 bg-center bg-no-repeat bg-cover"
        style={{
          backgroundImage: tenant?.config?.bg ? `url(${tenant.config.bg})` : 'url()',
          backgroundAttachment: 'fixed'
        }}
      >
        {shopTheme.overlay.enabled && (
          <div className="absolute inset-0 backdrop-blur-[2px]" style={{
            backgroundImage: shopTheme.overlay.useGradient
              ? `linear-gradient(${(
                  {
                    'to-b': 'to bottom',
                    'to-t': 'to top',
                    'to-r': 'to right',
                    'to-l': 'to left',
                    'to-tr': 'to top right',
                    'to-br': 'to bottom right',
                    'to-tl': 'to top left',
                    'to-bl': 'to bottom left',
                  } as Record<string, string>
                )[shopTheme.overlay.orientation] || 'to bottom'},
                  ${hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity1)},
                  ${hexToRgba(shopTheme.overlay.color2, shopTheme.overlay.opacity2)},
                  ${hexToRgba(shopTheme.overlay.color3, shopTheme.overlay.opacity3)}
                )`
              : undefined,
            backgroundColor: !shopTheme.overlay.useGradient
              ? hexToRgba(shopTheme.overlay.color1, shopTheme.overlay.opacity2)
              : undefined,
          }} />
        )}
      </div>

      {/* Particules flottantes avec couleurs et icônes personnalisables */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Top-Left */}
        {shopTheme.icons[0]?.enabled && (
          <motion.div
            className="absolute top-20 left-10"
            style={{ color: hexToRgba(shopTheme.icons[0].color, 0.2) }}
            animate={{ y: [-20, 20, -20], rotate: [0, 180, 360] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut" }}
          >
            {(() => {
              const IconComponent = iconMap[shopTheme.icons[0].icon] || iconMap.ShoppingBag;
              return IconComponent ? <IconComponent size={shopTheme.icons[0].size} /> : null;
            })()}
          </motion.div>
        )}
        {/* Top-Right */}
        {shopTheme.icons[1]?.enabled && (
          <motion.div
            className="absolute top-40 right-20"
            style={{ color: hexToRgba(shopTheme.icons[1].color, 0.2) }}
            animate={{ y: [20, -20, 20], rotate: [360, 180, 0] }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          >
            {(() => {
              const IconComponent = iconMap[shopTheme.icons[1].icon] || iconMap.Sparkles;
              return IconComponent ? <IconComponent size={shopTheme.icons[1].size} /> : null;
            })()}
          </motion.div>
        )}
        {/* Bottom-Left */}
        {shopTheme.icons[2]?.enabled && (
          <motion.div
            className="absolute bottom-40 left-20"
            style={{ color: hexToRgba(shopTheme.icons[2].color, 0.2) }}
            animate={{ y: [-15, 15, -15], rotate: [0, 90, 180] }}
            transition={{ duration: 12, repeat: Infinity, ease: "easeInOut" }}
          >
            {(() => {
              const IconComponent = iconMap[shopTheme.icons[2].icon] || iconMap.Diamond;
              return IconComponent ? <IconComponent size={shopTheme.icons[2].size} /> : null;
            })()}
          </motion.div>
        )}
        {/* Bottom-Right */}
        {shopTheme.icons[3]?.enabled && (
          <motion.div
            className="absolute bottom-20 right-10"
            style={{ color: hexToRgba(shopTheme.icons[3].color, 0.2) }}
            animate={{ y: [15, -15, 15], rotate: [180, 270, 360] }}
            transition={{ duration: 9, repeat: Infinity, ease: "easeInOut" }}
          >
            {(() => {
              const IconComponent = iconMap[shopTheme.icons[3].icon] || iconMap.Star;
              return IconComponent ? <IconComponent size={shopTheme.icons[3].size} /> : null;
            })()}
          </motion.div>
        )}
      </div>

      {/* Contenu de la page */}
      <div className="relative z-10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-12 md:pt-24 md:pb-20">
          {/* En-tête de la section - Style SIS */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, type: "spring", stiffness: 100 }}
            className="mb-8 py-16 text-center"
          >
            <motion.div 
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-8 py-4 mb-8 shadow-xl"
              style={{ borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d` }}
            >
              <ShoppingBag className="w-5 h-5 mr-3 animate-pulse" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
              <span className="font-semibold text-lg" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{shopHeader.badgeLabel}</span>
              <Sparkles className="w-4 h-4 ml-3 animate-bounce" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
            </motion.div>
            
            <motion.h1 
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              transition={{ duration: 0.8, delay: 0.4, type: "spring", stiffness: 80 }}
              className="text-4xl md:text-6xl font-bold text-white mb-4 drop-shadow-lg"
            >
              {shopHeader.title}
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.6 }}
              className="text-white/90 text-lg drop-shadow-md max-w-3xl mx-auto"
            >
              {shopHeader.line1}
              <br />
              <span style={{ color: tenant?.config?.color1 || '#fe8d17' }}>{shopHeader.line2}</span>
            </motion.p>
          </motion.div>

        {/* Contenu principal */}
        <div className="max-w-7xl mx-auto">
          <motion.div 
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="mb-12 bg-white/5 border border-white/10 text-white backdrop-blur-sm shadow-xl rounded-2xl p-8"
          >
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Input 
                  type="text" 
                  placeholder="Rechercher un produit, un service..." 
                  className="pr-10 bg-white/10 border border-white/20 focus:ring-2 focus:border-transparent outline-none transition-all duration-300 placeholder-white/50"
                  style={{ 
                    color: tenant?.config?.color3 || '#ffffff',
                    '--tw-ring-color': tenant?.config?.color3 || '#fe8d17'
                  } as React.CSSProperties}
                  value={searchInput} 
                  onChange={(e) => setSearchInput(e.target.value)} 
                />
                <Search size={18} className="absolute right-3 top-1/2 -translate-y-1/2" style={{ color: tenant?.config?.color3 || '#9ca3af' }} />
              </div>
              <Button 
                variant="outline" 
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)} 
                className="flex items-center gap-2 border-white/20 text-white hover:bg-white/10 transition-all duration-300"
                style={{ 
                  '--tw-border-color': tenant?.config?.color3 || '#fe8d17' + '33',
                  color: tenant?.config?.color3 || '#ffffff'
                } as React.CSSProperties}
              >
                <Filter size={16} /> Filtres avancés 
                <ChevronDown size={16} className={`transition-transform ${showAdvancedFilters ? 'rotate-180' : ''}`} />
              </Button>
            </div>
            
              {showAdvancedFilters && (
                <div className="mt-6 border-t pt-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6" style={{ borderColor: `${tenant?.config?.color3 || '#fe8d17'}33` }}>
                {/* Filtre par catégorie */}
                <div>
                    <h3 className="text-white font-medium mb-3 flex items-center gap-2">Catégories</h3>
                  <div className="space-y-2">
                    {(showAllCategories ? categories : categories.slice(0,5)).map((category) => (
                      <div key={category.id} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`category-${category.id}`} 
                          checked={selectedCategories.includes(category.id.toString())}
                          onCheckedChange={() => handleCategoryToggle(category.id.toString())}
                        />
                          <label 
                            htmlFor={`category-${category.id}`}
                            className="text-sm text-white cursor-pointer"
                          >
                          {category.name}
                        </label>
                      </div>
                    ))}
                    {categories.length > 5 && (
                      <button
                        type="button"
                        className="text-xs underline mt-1 transition-colors"
                        style={{ 
                          color: tenant?.config?.color1 || '#fe8d17',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = `${tenant?.config?.color1 || '#fe8d17'}cc`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
                        }}
                        onClick={() => setShowAllCategories(v => !v)}
                      >
                        {showAllCategories ? 'Voir moins' : `Voir plus (${categories.length - 5})`}
                      </button>
                    )}
                  </div>
                </div> 
                
                {/* Filtre par matériaux */}
                <div>
                    <h3 className="text-white font-medium mb-3 flex items-center gap-2">Matériaux</h3>
                  <div className="space-y-2">
                    {(showAllMaterials ? availableMaterials : availableMaterials.slice(0,5)).map((material) => (
                      <div key={material} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`material-${material}`} 
                          checked={selectedMaterials.includes(material)}
                          onCheckedChange={() => handleMaterialToggle(material)}
                        />
                          <label 
                            htmlFor={`material-${material}`}
                            className="text-sm text-white cursor-pointer"
                          >
                          {material}
                        </label>
                      </div>
                    ))}
                    {availableMaterials.length > 5 && (
                      <button
                        type="button"
                        className="text-xs underline mt-1 transition-colors"
                        style={{ 
                          color: tenant?.config?.color1 || '#fe8d17',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = `${tenant?.config?.color1 || '#fe8d17'}cc`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
                        }}
                        onClick={() => setShowAllMaterials(v => !v)}
                      >
                        {showAllMaterials ? 'Voir moins' : `Voir plus (${availableMaterials.length - 5})`}
                      </button>
                    )}
                  </div>
                </div> 
                
                {/* Filtre par année */}
                <div>
                    <h3 className="text-white font-medium mb-3 flex items-center gap-2">Année</h3>
                  <div className="space-y-2">
                    {(showAllYears ? availableYears : availableYears.slice(0,5)).map((year) => (
                      <div key={year} className="flex items-center space-x-2">
                        <Checkbox 
                          id={`year-${year}`} 
                          checked={selectedYears.includes(year)}
                          onCheckedChange={() => handleYearToggle(year)}
                        />
                        <label 
                          htmlFor={`year-${year}`}
                          className="text-sm text-white cursor-pointer"
                        >
                          {year}
                        </label>
                      </div>
                    ))}
                    {availableYears.length > 5 && (
                      <button
                        type="button"
                        className="text-xs underline mt-1 transition-colors"
                        style={{ 
                          color: tenant?.config?.color1 || '#fe8d17',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.color = `${tenant?.config?.color1 || '#fe8d17'}cc`;
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
                        }}
                        onClick={() => setShowAllYears(v => !v)}
                      >
                        {showAllYears ? 'Voir moins' : `Voir plus (${availableYears.length - 5})`}
                      </button>
                    )}
                  </div>
                </div>
                
                {/* Filtre par poids et quantité */}
                <div>
                  <h3 className="text-white font-medium mb-3">Poids (kg)</h3>
                  <div className="grid grid-cols-2 gap-2 mb-4">
                    <div>
                      <Input 
                        type="number" 
                        placeholder="Min" 
                        className="bg-white/10 border placeholder-white/50 h-8"
                        style={{ 
                          color: tenant?.config?.color3 || '#ffffff',
                          borderColor: `${tenant?.config?.color3 || '#fe8d17'}33`,
                          '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                        } as React.CSSProperties}
                        value={weightRange.min === null ? '' : weightRange.min} 
                        onChange={(e) => handleWeightRangeChange('min', e.target.value)} 
                      />
                    </div>
                    <div>
                      <Input 
                        type="number" 
                        placeholder="Max" 
                        className="bg-white/10 border placeholder-white/50 h-8"
                        style={{ 
                          color: tenant?.config?.color3 || '#ffffff',
                          borderColor: `${tenant?.config?.color3 || '#fe8d17'}33`,
                          '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                        } as React.CSSProperties}
                        value={weightRange.max === null ? '' : weightRange.max} 
                        onChange={(e) => handleWeightRangeChange('max', e.target.value)} 
                      />
                    </div>
                  </div>
                  
                  <h3 className="text-white font-medium mb-3">Quantité</h3>
                  <div className="grid grid-cols-2 gap-2">
                    <div>
                      <Input 
                        type="number" 
                        placeholder="Min" 
                        min="0"
                        className="bg-white/10 border placeholder-white/50 h-8"
                        style={{ 
                          color: tenant?.config?.color3 || '#ffffff',
                          borderColor: `${tenant?.config?.color3 || '#fe8d17'}33`,
                          '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                        } as React.CSSProperties}
                        value={stockRange.min === null ? '' : stockRange.min} 
                        onChange={(e) => handleStockRangeChange('min', e.target.value)} 
                      />
                    </div>
                    <div>
                      <Input 
                        type="number" 
                        placeholder="Max" 
                        min="0"
                        className="bg-white/10 border placeholder-white/50 h-8"
                        style={{ 
                          color: tenant?.config?.color3 || '#ffffff',
                          borderColor: `${tenant?.config?.color3 || '#fe8d17'}33`,
                          '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                        } as React.CSSProperties}
                        value={stockRange.max === null ? '' : stockRange.max} 
                        onChange={(e) => handleStockRangeChange('max', e.target.value)} 
                      />
                    </div>
                  </div>
                </div> 
                
                {/* Filtres supplémentaires */}
                <div>
                  <h3 className="text-white font-medium mb-3">Autres filtres</h3>
                  
                  {/* Filtre par prix */}
                  <div className="mb-4">
                    <h4 className="text-white/80 text-sm mb-2">Prix (FCFA)</h4>
                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Input 
                          type="number" 
                          placeholder="Min" 
                          className="bg-white/10 border placeholder-white/50 h-8"
                          style={{ 
                            color: tenant?.config?.color3 || '#ffffff',
                            borderColor: `${tenant?.config?.color3 || '#fe8d17'}33`,
                            '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                          } as React.CSSProperties}
                          value={priceRange.min === null ? '' : priceRange.min} 
                          onChange={(e) => handlePriceRangeChange('min', e.target.value)} 
                        />
                      </div>
                      <div>
                        <Input 
                          type="number" 
                          placeholder="Max" 
                          className="bg-white/10 border placeholder-white/50 h-8"
                          style={{ 
                            color: tenant?.config?.color3 || '#ffffff',
                            borderColor: `${tenant?.config?.color3 || '#fe8d17'}33`,
                            '--tw-ring-color': tenant?.config?.color1 || '#fe8d17'
                          } as React.CSSProperties}
                          value={priceRange.max === null ? '' : priceRange.max} 
                          onChange={(e) => handlePriceRangeChange('max', e.target.value)} 
                        />
                      </div>
                    </div>
                  </div>
                  
                  {/* Filtre par type de produit */}
                  <div>
                    <h4 className="text-white/80 text-sm mb-2">Type de produit</h4>
                    <div className="space-y-2">
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="unique-true" 
                          checked={isUniqueOnly === true}
                          onCheckedChange={() => handleUniqueToggle(true)}
                        />
                        <label 
                          htmlFor="unique-true"
                          className="text-sm text-white cursor-pointer"
                        >
                          Produits uniques
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <Checkbox 
                          id="unique-false" 
                          checked={isUniqueOnly === false}
                          onCheckedChange={() => handleUniqueToggle(false)}
                        />
                        <label 
                          htmlFor="unique-false"
                          className="text-sm text-white cursor-pointer"
                        >
                          Éditions limitées
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            
            {hasActiveFilters && (
              <div className="mt-6 border-t border-white/10 pt-4 flex items-center justify-between">
                <span className="text-white/80 text-sm">{filteredProducts.length} produit(s) trouvé(s)</span>
                <Button onClick={resetFilters} variant="link" className="p-0 h-auto transition-colors"
                  style={{ 
                    color: tenant?.config?.color1 || '#fe8d17',
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.color = `${tenant?.config?.color1 || '#fe8d17'}cc`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
                  }}
                >Réinitialiser</Button>
              </div>
            )}
            </motion.div>
          {isLoading ? (
            <div className="flex flex-col items-center justify-center text-white py-20">
              <Loader2 className="w-12 h-12 animate-spin" style={{ color: tenant?.config?.color1 || '#fe8d17' }} />
              <p className="mt-4 text-lg">Chargement des produits...</p>
            </div>
          ) : filteredProducts.length === 0 ? (
            <div className="text-center py-20 bg-white/5 backdrop-blur-sm rounded-lg border border-white/10">
              <h2 className="text-2xl font-bold mb-4 text-white">Aucun produit ne correspond à vos critères</h2>
              <p className="mb-6 text-white/80">Essayez de modifier vos filtres pour découvrir d'autres produits.</p>
              <Button onClick={resetFilters} className="font-bold"
                style={{ 
                  backgroundColor: tenant?.config?.color1 || '#fe8d17',
                  color: tenant?.config?.color3 || '#ffffff',
                  backgroundImage: `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#e07a10'}, ${tenant?.config?.color1 || '#fe8d17'})`;
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundImage = `linear-gradient(to right, ${tenant?.config?.color1 || '#fe8d17'}, ${tenant?.config?.color1 || '#e47c15'})`;
                }}
              >
                <X className="mr-2 h-4 w-4" />
                Réinitialiser les filtres
              </Button>
            </div>
          ) : (
            <>
              <motion.div 
                className={gridClassName}
                style={{ gap: `${gridConfig.gap}px` }}
                variants={containerVariants}
                initial="hidden"
                animate="visible"
              >
                {filteredProducts.map((product) => (
                  <motion.div key={product.id} variants={cardVariants}>
                    <ProductCard product={product} />
                  </motion.div>
                ))}
              </motion.div>

              {totalPages > 1 && (
                <div className="flex justify-center mt-12">
                  <nav className="flex items-center gap-4 bg-white/5 backdrop-blur-sm rounded-full px-6 py-3 border border-white/10">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleFilterChange('page', String(page - 1))}
                      disabled={page <= 1}
                      className="border-white/20 text-white transition-all duration-300"
                      style={{
                        '--tw-border-color': tenant?.config?.color1 || '#fe8d17' + '33',
                        '--tw-bg-opacity': '0.2',
                        color: tenant?.config?.color3 || '#ffffff'
                      } as React.CSSProperties}
                      onMouseEnter={(e) => {
                        if (!e.currentTarget.disabled) {
                          e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}33`;
                          e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!e.currentTarget.disabled) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}33`;
                        }
                      }}
                    >
                      Précédent
                    </Button>
                    <span className="text-white font-medium px-4 py-2 rounded-full border"
                      style={{
                        backgroundColor: `${tenant?.config?.color1 || '#fe8d17'}33`,
                        borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
                        color: tenant?.config?.color3 || '#ffffff'
                      }}
                    >
                      Page {page} sur {totalPages}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleFilterChange('page', String(page + 1))}
                      disabled={page >= totalPages}
                      className="border-white/20 text-white transition-all duration-300"
                      style={{
                        '--tw-border-color': tenant?.config?.color1 || '#fe8d17' + '33',
                        '--tw-bg-opacity': '0.2',
                        color: tenant?.config?.color3 || '#ffffff'
                      } as React.CSSProperties}
                      onMouseEnter={(e) => {
                        if (!e.currentTarget.disabled) {
                          e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}33`;
                          e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!e.currentTarget.disabled) {
                          e.currentTarget.style.backgroundColor = 'transparent';
                          e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}33`;
                        }
                      }}
                    >
                      Suivant
                    </Button>
                  </nav>
                </div>
              )}
            </>
          )}
          </div>
        </div>
      </div>
      </div>
    </PublicLayout>
  );
}

function MiniCartPreview() {
  const { tenant } = useTenantContext();
  const [cart, setCart] = React.useState<any[]>([]);
  const [open, setOpen] = React.useState(false);
  React.useEffect(() => {
    const updateCart = () => {
      const cartString = localStorage.getItem('shop_cart');
      setCart(cartString ? JSON.parse(cartString) : []);
    };
    updateCart();
    window.addEventListener('storage', updateCart);
    window.addEventListener('cart-updated', updateCart);
    return () => {
      window.removeEventListener('storage', updateCart);
      window.removeEventListener('cart-updated', updateCart);
    };
  }, []);
  return (
    <div className="fixed top-32 md:top-24 right-4 md:right-10 z-50">
      <button
        aria-label="Aperçu du panier"
        className="relative backdrop-blur-sm rounded-full p-3 shadow-lg hover:scale-110 transition-all duration-300"
        style={{
          backgroundColor: `${tenant?.config?.color2 || '#08062b'}e6`,
          borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.backgroundColor = tenant?.config?.color1 || '#fe8d17';
          e.currentTarget.style.borderColor = tenant?.config?.color1 || '#fe8d17';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.backgroundColor = `${tenant?.config?.color2 || '#08062b'}e6`;
          e.currentTarget.style.borderColor = `${tenant?.config?.color1 || '#fe8d17'}4d`;
        }}
        onClick={() => setOpen(v => !v)}
      >
        <svg className="w-6 h-6 text-white transition-colors" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2 9m13-9l2 9m-5-9V6a2 2 0 10-4 0v3m4 0h-4" /></svg>
        {cart.length > 0 && <span className="absolute -top-1 -right-1 rounded-full text-xs px-2 py-0.5 font-bold animate-pulse"
          style={{
            backgroundColor: tenant?.config?.color1 || '#fe8d17',
            color: tenant?.config?.color3 || '#ffffff'
          }}
        >{cart.length}</span>}
      </button>
      {open && (
        <div className="mt-2 w-80 backdrop-blur-sm rounded-xl shadow-2xl p-4 relative"
          style={{
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
            borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`,
            color: tenant?.config?.color3 || '#ffffff'
          }}
        >
          <button
            aria-label="Fermer le mini-panier"
            className="absolute top-2 right-2 text-xl font-bold transition-colors"
            style={{
              color: `${tenant?.config?.color4 || '#9ca3af'}b3`
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = `${tenant?.config?.color4 || '#9ca3af'}b3`;
            }}
            onClick={() => setOpen(false)}
            tabIndex={0}
          >×</button>
          <div className="flex justify-between items-center mb-2 pr-6">
            <span className="font-bold" style={{ color: tenant?.config?.color3 || '#ffffff' }}>Mon panier</span>
            <Link to="/boutique/panier" className="hover:underline transition-colors"
              style={{
                color: tenant?.config?.color1 || '#fe8d17'
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = `${tenant?.config?.color1 || '#fe8d17'}cc`;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
              }}
            >Voir tout</Link>
          </div>
          {cart.length === 0 ? (
            <div className="text-sm" style={{ color: `${tenant?.config?.color4 || '#9ca3af'}99` }}>Votre panier est vide.</div>
          ) : (
            <ul style={{ borderColor: `${tenant?.config?.color1 || '#fe8d17'}33` }} className="divide-y">
              {cart.map(item => (
                <li key={item.productId} className="py-2 grid grid-cols-6 items-center gap-2">
                  {/* Image 1/6 */}
                  <div className="col-span-1 flex items-center">
                    {item.imageUrl ? (
                      <img
                        src={item.imageUrl}
                        alt={item.title}
                        className="w-10 h-10 rounded object-cover"
                        style={{
                          backgroundColor: `${tenant?.config?.color1 || '#fe8d17'}1a`,
                          borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`
                        }}
                        onError={e => { e.currentTarget.src = item.imageUrl || 'https://via.placeholder.com/400x400'; }}
                        aria-label={"Image de " + item.title}
                      />
                    ) : (
                      <div className="w-10 h-10 flex items-center justify-center rounded"
                        style={{
                          backgroundColor: `${tenant?.config?.color1 || '#fe8d17'}1a`,
                          borderColor: `${tenant?.config?.color1 || '#fe8d17'}4d`
                        }}
                      >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" style={{ color: `${tenant?.config?.color4 || '#9ca3af'}99` }}><path strokeLinecap="round" strokeLinejoin="round" d="M3 3h2l.4 2M7 13h10l4-8H5.4M7 13L5.4 5M7 13l-2 9m13-9l2 9m-5-9V6a2 2 0 10-4 0v3m4 0h-4" /></svg>
                      </div>
                    )}
                  </div>
                  {/* Titre + quantité 3/6 */}
                  <div className="col-span-3 min-w-0">
                    <div className="font-semibold truncate" style={{ color: tenant?.config?.color3 || '#ffffff' }}>{item.title}</div>
                    <div className="text-xs" style={{ color: `${tenant?.config?.color4 || '#9ca3af'}b3` }}>x{item.quantity}</div>
                  </div>
                  {/* Prix 1/6 */}
                  <div className="col-span-1 text-sm font-bold text-right" style={{ color: tenant?.config?.color1 || '#fe8d17' }}>
                    {new Intl.NumberFormat('fr-FR').format(item.price * item.quantity)} FCFA
                  </div>
                  {/* Icône suppression 1/6 */}
                  <div className="col-span-1 flex justify-end">
                    <button
                      aria-label="Retirer du panier"
                      className="p-1 transition-colors rounded hover:bg-opacity-20"
                      style={{
                        color: `${tenant?.config?.color4 || '#9ca3af'}66`
                      }}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.color = tenant?.config?.color1 || '#fe8d17';
                        e.currentTarget.style.backgroundColor = `${tenant?.config?.color1 || '#fe8d17'}1a`;
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.color = `${tenant?.config?.color4 || '#9ca3af'}66`;
                        e.currentTarget.style.backgroundColor = 'transparent';
                      }}
                      onClick={() => {
                        const updated = cart.filter(i => i.productId !== item.productId);
                        setCart(updated);
                        localStorage.setItem('shop_cart', JSON.stringify(updated));
                      }}
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3m5 0H6" /></svg>
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}
    </div>
  );
}

export default function ProductListPageWithCart() {
  return (
    <>
      <ProductListPage />
      <MiniCartPreview />
    </>
  );
}
